/**
 * Component to provide settings for line and table analysis
 * 1. Prompt selection as personal or central
 * 2. Q&A option
 * 3. Documents option
 * Created on 10/09/2024
 * Modified on 07/07/2025
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

//  Motif controls
import {
  MotifCheckbox,
  MotifLabel,
  MotifToast,
  MotifTooltip,
  MotifMessage,
  MotifButton,
  MotifFormField,
  MotifTable,
} from "@ey-xd/motif-react";
import MotifModal, {
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
} from "@ey-xd/motif-react/Modal";
import MotifSearch from "@ey-xd/motif-react/Search";
import MotifRadioButton, {
  MotifRadioButtonGroup,
} from "@ey-xd/motif-react/RadioButton";
import MotifSelect, { MotifOption } from "@ey-xd/motif-react/Select";
import MotifTabNavigation, {
  MotifTabControl,
} from "@ey-xd/motif-react/TabNavigation";

//  Icons imports
import iconInfo from "../../../../assets/images/information.svg";
import iconWarning from "../../../../assets/images/warning-triangle.svg";
import iconArrowBack from "../../../../assets/images/arrow_back.svg";
import iconInfoEmpty from "../../../../assets/images/info-empty.svg";
import iconSearch from "../../../../assets/images/search.svg";
import iconPageSearch from "../../../../assets/images/page-search.svg";
import iconCheckCircle from "../../../../assets/images/check-circle.svg";
// import iconHelpCircle from "../../../../assets/images/help-circle.svg";

//  Internal imports
import "./AnalysisSettings.scss";
import DocumentService from "../../../../services/documentService";
import AnalysisService from "../../../../services/analysisService";
import TableService from "../../../../services/tableService";
import { analysisSettingsConfig } from "../../../../config/analysisSettingsConfig";

const CheckIconCell = () => {
  return (
    <img className="icon-check-green" src={iconCheckCircle} alt="Completed" />
  );
};

const AnalysisSettings = ({
  initSource,
  openModal,
  setOpenModal,
  fromUploadProcess,
  setOpenModalForUploadProgress,
  closeModal,
  onConfirm,
  lineAnalysisStatus,
  documentList,
  isCallForTableAnalysis,
  selectedTable,
  exportedFileName,
  receiveUploadQnAStatus,
  setDisplayUploadQAModal,
  setUploadQALoader,
  checkRowIds,
  setSelectedAnalysisValue,
  selectedAnalysisValue,
  rowStatusData,
  selectedQAValue,
  setSelectedQAValue,
  completedItems,
  selectedLineItemsRowIds,
  setSelectedLineItemsRowIds,
}) => {
  const promptTypeRef = [
    {
      id: 1,
      key: "Central",
      value: "Central",
      label: "Central",
    },
    {
      id: 2,
      key: "Personal",
      value: "Personal",
      label: "Personal",
    },
  ];
  const vTabs = [
    {
      id: 1,
      elementId: "idTabContentActive",
      title: "Active",
    },
    {
      id: 2,
      elementId: "idTabContentDeleted",
      title: "Deleted",
    },
  ];
  const vTabIds = {
    active: "idTabContentActive",
    deleted: "idTabContentDeleted",
  };
  const [selectedPromptType, setSelectedPromptType] = useState("Central");
  const [analysisSettings, setAnalysisSettings] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedPromptName, setSelectedPromptName] = useState("");
  const [isMultiplePrompts, setIsMultiplePrompts] = useState(false);
  const [useQnA, setUseQnA] = useState(true);
  const [excelOutput] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [delDocuments, setDelDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const [currentStep, setCurrentStep] = useState(1);
  const [showMessage, setShowMessage] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const [isCheckBoxDisabled, setIsCheckBoxDisabled] = useState(false);
  const [notesData, setNotesData] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const documentService = new DocumentService();
  const analysisService = new AnalysisService();

  const { user, activeProject } = useSelector((state) => state.user);
  const userEmailId = user.username;
  const selectedProjectId = activeProject.project_UUID;
  const tableService = new TableService();
  let projectCategory = "";
  if (activeProject?.category === "1") {
    projectCategory = "client";
  } else if (activeProject?.category === "2") {
    projectCategory = "testing";
  } else if (activeProject?.category === "3") {
    projectCategory = "testing_Project_Admin";
  }

  const [colDefs] = useState([
    {
      field: "RowHeader",
      headerName: "Line Name",
      headerCheckboxSelection: true,
      checkboxSelection: true,
      wrapText: true,
      autoHeight: true,
      width: 500,
      flex: 2,
      cellRenderer: (params) => {
        return params.data.RowHeader;
      },
    },
    {
      field: "status",
      headerName: "Status",
      wrapText: true,
      maxWidth: 105,
      autoHeight: true,
      flex: 2,
      cellRenderer: CheckIconCell,
    },
  ]);

  useEffect(() => {
    if (initSource === "upload") {
      setSelectedAnalysisValue("qa");
      setSelectedQAValue("useExist");
    }
    getNotes();
    // eslint-disable-next-line
  }, [initSource]);

  useEffect(() => {
    const hasExistingQnA = checkRowIds.some((rowId) =>
      rowStatusData.some(
        (row) => row.rowId === rowId && row.existingQnA === true
      )
    );

    if (initSource === "upload") {
      handleChangeQA("useExist");
      onUseQnAChange({ target: { checked: false } });
    } else if (selectedAnalysisValue === "qa" && !hasExistingQnA) {
      handleChangeQA("genNew");
      onUseQnAChange({ target: { checked: true } });
    }
    // eslint-disable-next-line
  }, []);

  const handleChangeAnalysis = (value) => {
    if (
      (value === "notes") &&
      (
        (notesData.length === 0) ||
        (initSource === "upload") ||
        !checkRowIds.some((rowId) =>
          notesData.some((note) => note.rowId === rowId)
        )
      )
    ) {
      setSelectedAnalysisValue("qa");
    } else {
      setSelectedAnalysisValue(value);
    }
    getNotes();
  };

  const handleChangeQA = (value) => {
    const shouldForceUseExist = initSource === "upload";

    if (
      value === "useExist" &&
      isDisabled("useExist") &&
      !shouldForceUseExist
    ) {
      setSelectedQAValue("genNew");
      onUseQnAChange({ target: { checked: true } });
    } else {
      setSelectedQAValue(shouldForceUseExist ? "useExist" : value);
      onUseQnAChange({ target: { checked: value === "genNew" } });
    }
  };

  function isDisabled(option) {
    return (
      option === "useExist" &&
      !checkRowIds.some((rowId) =>
        rowStatusData.some(
          (row) => row.rowId === rowId && row.existingQnA === true
        )
      )
    );
  }

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const filterdDocuments = documents.filter(
    (doc) =>
      doc.status === "Complete" &&
      doc.documentName.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDocuments(filterdDocuments);
    } else {
      setSelectedDocuments([]);
    }
  };

  /**
   * This asynchronous function retrieves document data from the `documentService` using
   * the `selectedProjectId` provided in `props`. Once the data is fetched, it updates the `documents`
   * state with the retrieved data.
   */
  const getDocuments = async () => {
    const docData = await documentService.getDocuments(selectedProjectId);
    const vActiveDocs = docData.filter(
      (element) => element.status === "Complete"
    );
    const vDeletedDocs = docData.filter(
      (element) => element.status === "Deleted"
    );
    setDocuments(vActiveDocs);
    setDelDocuments(vDeletedDocs);
    populateSelectedDocuments(vActiveDocs);
  };

  const populateSelectedDocuments = (vDocs) => {
    if (
      documentList &&
      typeof documentList !== "string" &&
      documentList.length > 0
    ) {
      let tmpDocs = [];
      documentList.forEach((element) => {
        let vDoc;
        if (typeof element === "string") {
          vDoc = vDocs.find(
            (ele) => ele.documentName === element && ele.status === "Complete"
          );
        } else if (typeof element === "object") {
          vDoc = vDocs.find(
            (ele) =>
              (ele.document_UUID === element.id ||
                ele.documentId === element.id) &&
              ele.status === "Complete"
          );
        }
        if (vDoc) {
          tmpDocs.push(vDoc);
        }
      });
      setSelectedDocuments(tmpDocs);
    }
  };

  useEffect(() => {
    if (!isCallForTableAnalysis) {
      if (lineAnalysisStatus === "In-Progress") {
        setIsNextButtonDisabled(false);
        setIsConfirmDisabled(false);
        setIsCheckBoxDisabled(true);
        setShowMessage(true);
      } else if (
        lineAnalysisStatus === "Completed" ||
        lineAnalysisStatus === "No Analysis Initiated"
      ) {
        setIsNextButtonDisabled(false);
        setIsConfirmDisabled(false);
        setIsCheckBoxDisabled(false);
      } else {
        setIsNextButtonDisabled(true);
        setIsConfirmDisabled(true);
      }
    } else {
      setIsConfirmDisabled(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isCallForTableAnalysis) {
      getDocuments();
      if (selectedDocuments.length > 0) {
        if (documents.length === selectedDocuments.length) {
          setShowMessage(true);
        } else {
          setShowMessage(false);
        }
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getAnalysisSettings();
    // eslint-disable-next-line
  }, []);

  const getAnalysisSettings = () => {
    const vData = {
      userId: userEmailId,
      projectId: selectedProjectId,
      tableId: selectedTable.id,
      tableType: selectedTable.tableType,
      projectCategory: projectCategory,
    };
    analysisService.getAnalysisSettings(vData).then((response) => {
      if (response) {
        setAnalysisSettings(response);
        if (response?.centralPrompt[0]) {
          setSelectedPrompt(response?.centralPrompt[0]);
        }
      }
    });
  };

  /**
   * This function maintain state for selected documents from list
   * @param {*} e : Click event
   */
  const handleDocumentSelectList = (vDoc) => {
    let vDocs;
    if (selectedDocuments && selectedDocuments.length > 0) {
      const docIndex = selectedDocuments.findIndex(
        (element) => element.document_UUID === vDoc.document_UUID
      );
      if (docIndex !== -1) {
        vDocs = selectedDocuments.filter(
          (element) => element.document_UUID !== vDoc.document_UUID
        );
      } else {
        vDocs = [...selectedDocuments, vDoc];
      }
    } else {
      vDocs = [...selectedDocuments, vDoc];
    }
    if (vDocs.length > 0) {
      if (documents.length === vDocs.length) {
        setShowMessage(true);
      } else {
        setShowMessage(false);
      }
    }
    setSelectedDocuments(vDocs);
  };

  /**
   * Handles the Next button click
   */
  const handleNextClick = () => {
    setCurrentStep(2);
  };

  /**
   * Handles the Back button click
   */
  const handleBackClick = () => {
    setCurrentStep(1);
  };

  /**
   * Handles the close button click of the popup
   */
  const onToastcloseclick = () => {
    setShowMessage(false);
  };

  /**
   * Confirm button click, sends the selected document data and Line Comeentary Excel check box value
   */
  const handleConfirmClick = () => {
    let data;
    if (!isCallForTableAnalysis) {
      data = {
        promptIds: {
          userPromptId: selectedPrompt.id,
          systemPromptId: analysisSettings?.systemPrompt[0]
            ? analysisSettings?.systemPrompt[0]
            : "",
          promptType: selectedPromptType
            ? selectedPromptType.toLowerCase()
            : undefined,
        },
        isCustom: selectedAnalysisValue === "custom",
        qnaFlag: selectedAnalysisValue === "qa",
        NotesFlag: checkRowIds.map((rowId) => {
          const notesExists = notesData.findIndex((row) => row.rowId === rowId);
          return (notesExists !== -1);
        }),
        isCommentaryExcel: excelOutput,
        documents: selectedDocuments
      };
    } else {
      data = {
        promptIds: {
          userPromptId: selectedPrompt.id,
          systemPromptId: analysisSettings?.systemPrompt[0]
            ? analysisSettings?.systemPrompt[0]
            : "",
          promptType: selectedPromptType
            ? selectedPromptType.toLowerCase()
            : undefined,
        },
        isCommentaryExcel: excelOutput,
      };
    }
    data['projectCategory'] = projectCategory;
    onConfirm(data);
  };

  const handleConfirmForUploadQANFlow = () => {
    updateQADocAndAnalysisSettings();
  };

  const onPromptTypeChange = (vValue) => {
    setSelectedPromptType(vValue);
    if (vValue === "Central") {
      setSelectedPrompt(analysisSettings?.centralPrompt[0]);
      setIsMultiplePrompts(false);
    } else if (
      vValue === "Personal" &&
      analysisSettings?.personalPrompt.length > 1
    ) {
      setIsMultiplePrompts(true);
      setSelectedPrompt(null);
    } else if (
      vValue === "Personal" &&
      analysisSettings?.personalPrompt.length < 2
    ) {
      setIsMultiplePrompts(false);
      setSelectedPrompt(analysisSettings?.personalPrompt[0]);
    }
  };

  const onUseQnAChange = (e) => {
    setUseQnA(e.target.checked);
  };

  const onPromptNameChange = (data) => {
    setSelectedPrompt(data);
    setSelectedPromptName(data.promptName);
  };

  /**
   * Function to switch tabs
   * @param {*} tab
   */
  const handleTabClick = (tab) => {
    switch (tab.elementId) {
      case vTabIds.active:
        document.getElementById(vTabIds.active).style.display = "block";
        document.getElementById(vTabIds.deleted).style.display = "none";
        break;
      case vTabIds.deleted:
        document.getElementById(vTabIds.active).style.display = "none";
        document.getElementById(vTabIds.deleted).style.display = "block";
        break;
      default:
    }
  };

  const getModalTitle = () => {
    let vModalTitle = analysisSettingsConfig?.modalTitle?.tableAnalysis;
    if (
      (currentStep === 1 && !isCallForTableAnalysis) ||
      (currentStep === 2 && !isCallForTableAnalysis)
    ) {
      vModalTitle = analysisSettingsConfig?.modalTitle?.lineAnalysis;
    }
    return vModalTitle;
  };

  const updateQADocAndAnalysisSettings = async () => {
    const documentIdList = [];
    const documentList = [];
    selectedDocuments.forEach((document) => {
      documentIdList.push({
        id: document.document_UUID, //  documentId
        docName: document.documentName,
      });
      documentList.push(document.documentName);
    });
    let data = {
      tableId: selectedTable.id,
      projectId: selectedProjectId,
      userId: userEmailId,
      filename: exportedFileName,
      qnaFlag: useQnA || initSource === "upload",
      documentIdList: documentIdList,
      documentList: documentList,
      isCommentaryExcel: excelOutput,
      promptIds: {
        userPromptId: selectedPrompt.id,
        systemPromptId: analysisSettings?.systemPrompt[0]
          ? analysisSettings?.systemPrompt[0]
          : "",
        promptType: selectedPromptType
          ? selectedPromptType.toLowerCase()
          : undefined,
      },
      projectCategory: projectCategory
    };
    setOpenModalForUploadProgress(false);
    setDisplayUploadQAModal(false);
    setUploadQALoader(true);
    let response = await documentService.updateQADoc(data);
    if (response) {
      receiveUploadQnAStatus(response.status.toLowerCase());
    } else {
      receiveUploadQnAStatus(null);
    }
  };

  const cancelUploadQADoc = async () => {
    let response = await documentService.cancelUploadQADoc(
      user.username,
      activeProject.project_UUID,
      selectedTable.id
    );
    if (response) {
      if (fromUploadProcess) {
        setOpenModalForUploadProgress(false);
      }
    }
  };

  const onClose = () => {
    if (fromUploadProcess) {
      cancelUploadQADoc();
      setDisplayUploadQAModal(false);
    } else {
      setOpenModal(!openModal);
      setSelectedAnalysisValue(null);
      setSelectedQAValue(null);
      setSelectedPrompt(null);
    }
  };

  useEffect(() => {
    getNotes();
    //  eslint-disable-next-line
  }, []);

  const getNotes = async () => {
    const requestData = {
      tableId: selectedTable.id,
    };
    try {
      const response = await tableService.getNotes(requestData);
      if (response) {
        const processedNotesData = loadExistingNotesData(response);
        setNotesData(processedNotesData);
      } else {
        throw new Error(response?.output || "Unknown error");
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const loadExistingNotesData = (notesData) => {
    if (notesData) {
      return notesData
        .filter((note) => (note.notes && (note.notes !== '')))
        .map((note) => ({
          RowHeader: note.RowHeader,
          notes_range: note.range,
          rowId: note.rowId,
        }));
    }

    return [];
  };

  /**
   * Function to get tooltip text for confirm button on Notes flow.
   */
  const getNotesConfirmBtnTooltipText = () => {
    let vNotesConfirmBtnTooltipText = "";
    if (
      !(
        !selectedAnalysisValue ||
        !selectedPromptType ||
        !selectedPrompt ||
        isConfirmDisabled
      )
    ) {
      vNotesConfirmBtnTooltipText = analysisSettingsConfig?.msgTooltipConfirm;
    } else if (!selectedPrompt) {
      vNotesConfirmBtnTooltipText = analysisSettingsConfig?.msgTooltipSelectPrompt;
    }
    return vNotesConfirmBtnTooltipText;
  };

  /**
   * Sync component variable while line items selection or de-selection
   * @param {*} event
   */
  const onLineItemSelection = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedRowIds = selectedRows.map((row) => row.rowId);
    setSelectedLineItemsRowIds(selectedRowIds);
    for (const element of completedItems) {
      if (selectedRows.findIndex((row) => row.rowId === element.rowId) !== -1) {
        element.selected = true;
      } else {
        element.selected = false;
      }
    }
  };

  /**
   * Reset line items selections
   */
  useEffect(() => {
    return () => {
      setSelectedLineItemsRowIds([]);
      for (const element of completedItems) {
        element.selected = undefined;
      }
    };
    //  eslint-disable-next-line
  }, []);

  return (
    <MotifModal
      show={openModal}
      onClose={onClose}
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
      className="analysisSettingsModal genericModal modalMotifFooterModal"
    >
      <MotifModalHeader
        closeButtonProps={{
          "aria-label": "Close",
          title: "Close",
        }}
        className="analysisSettingsHeader"
      >
        <div className="analysisSettingsHead">{getModalTitle()}</div>
      </MotifModalHeader>
      <MotifModalBody className="analysisSettingsBody">
        {currentStep === 2 && !isCallForTableAnalysis && (
          <div>
            <p className="document-list-header">{analysisSettingsConfig?.modalSubTitle?.documents}</p>
          </div>
        )}
        {currentStep === 1 && isCallForTableAnalysis && (
          <>
            <div className="fldPromptType table-prompt">
              <MotifFormField>
                <MotifLabel className="fldLabel">
                  {analysisSettingsConfig?.lblPromptLibrary}
                </MotifLabel>
                <MotifRadioButtonGroup className="fldPromptTypeGroup">
                  {promptTypeRef.map((element) => (
                    <div
                      key={element.id}
                      className="promptTypeOptionWrapper prompt-div"
                    >
                      <div>
                        <MotifRadioButton
                          id={"fldPromptType" + element.key}
                          name="fldPromptTypeOption"
                          onChange={() => onPromptTypeChange(element.value)}
                          value={element.value}
                          className="fldPromptTypeOption"
                          checked={selectedPromptType === element.value}
                          disabled={
                            element.key === "Personal" &&
                            analysisSettings?.personalPrompt.length < 1
                          }
                        >
                          <span
                            className={
                              element.key === "Personal" &&
                              analysisSettings?.personalPrompt.length < 1
                                ? "promptTypeOptionValue"
                                : ""
                            }
                          >
                            {element.label}
                          </span>
                        </MotifRadioButton>
                      </div>
                      <div>
                        {element.key === "Personal" &&
                          analysisSettings?.personalPrompt.length < 1 && (
                            <MotifTooltip
                              trigger={
                                <img
                                  src={iconInfoEmpty}
                                  alt="info"
                                  className="iconInfo"
                                />
                              }
                              allowHover={true}
                              allowClick={true}
                              hideCloseButton={true}
                              placement="right"
                              contentClassName="tooltipWithoutClose prompt-tooltip-no"
                            >
                              {analysisSettingsConfig?.msgNoPersonalPrompts}
                            </MotifTooltip>
                          )}
                      </div>
                    </div>
                  ))}
                </MotifRadioButtonGroup>
              </MotifFormField>
            </div>
            <div className="fldPromptName">
              <MotifFormField>
                <MotifLabel className="fldLabel">{analysisSettingsConfig?.lblPromptName}</MotifLabel>
                {!isMultiplePrompts && (
                  <span className="fldValPromptName">
                    {selectedPrompt?.promptName}
                  </span>
                )}
                {isMultiplePrompts &&
                  analysisSettings?.personalPrompt.length > 1 && (
                    <MotifSelect
                      visibleOptions={3}
                      placeholder="Select prompt"
                      className="fldPromptNameSelect"
                      value={selectedPromptName}
                    >
                      {analysisSettings.personalPrompt.map((element) => (
                        <MotifOption
                          onClick={() => onPromptNameChange(element)}
                          value={element?.promptName}
                          key={element.id}
                        >
                          {element.promptName}
                        </MotifOption>
                      ))}
                    </MotifSelect>
                  )}
              </MotifFormField>
            </div>
          </>
        )}
        {currentStep === 1 && !isCallForTableAnalysis && (
          <>
            <div className="radio-qanotes-prompt">
              <MotifLabel className="analuysis-flow-text">
                {analysisSettingsConfig?.lblAnalysis}
              </MotifLabel>
              <MotifRadioButtonGroup className="radio-button-group-analysis ">
                <MotifRadioButton
                  className="txt-qa-notes-radio"
                  value="notes"
                  checked={selectedAnalysisValue === "notes"}
                  disabled={
                    notesData.length === 0 ||
                    initSource === "upload" ||
                    !checkRowIds.some((rowId) =>
                      notesData.some((note) => note.rowId === rowId)
                    )
                  }
                  onChange={() => handleChangeAnalysis("notes")}
                >
                  {analysisSettingsConfig?.lblOptionNotes}
                </MotifRadioButton>
                <MotifRadioButton
                  className="txt-qa-notes-radio"
                  value="qa"
                  checked={
                    initSource === "upload" || selectedAnalysisValue === "qa"
                  }
                  onChange={() => handleChangeAnalysis("qa")}
                >
                  {analysisSettingsConfig?.lblOptionQA}
                </MotifRadioButton>
                {/* <div className="fldPromptTypeRadio">
                  <MotifRadioButton
                    className="txt-qa-notes-radio"
                    value="custom"
                    checked={selectedAnalysisValue === "custom"}
                    disabled={initSource === "upload"}
                    onChange={() => handleChangeAnalysis("custom")}
                  >
                    {analysisSettingsConfig.lblOptionCustom}
                  </MotifRadioButton>
                  <MotifTooltip
                    className="tooltip-qa"
                    trigger={
                      <img
                        src={iconHelpCircle}
                        alt="info"
                        className="iconInfo"
                      />
                    }
                    allowClick={true}
                    hideCloseButton={true}
                    placement="right"
                    contentClassName="tooltipWithoutClose custom-tooltip"
                  >
                    <div className="tooltip-hover-div">
                      {analysisSettingsConfig?.msgTooltipOptionCustom}
                    </div>
                  </MotifTooltip>
                </div> */}
              </MotifRadioButtonGroup>
            </div>
            {selectedAnalysisValue === "qa" && (
              <div className="fldPromptType radio-qanotes-prompt">
                <MotifFormField className="radio-qanotes-prompt">
                  <MotifLabel className="fldLabel">{analysisSettingsConfig?.lblOptionUseQA}</MotifLabel>
                  <MotifRadioButtonGroup className="fldPromptTypeGroup radio-qanotes-prompt">
                    <div className="fldPromptTypeRadio">
                      <MotifRadioButton
                        className="fldPromptTypeOption"
                        value="genNew"
                        checked={selectedQAValue === "genNew"}
                        disabled={initSource === "upload"}
                        onChange={() => {
                          handleChangeQA("genNew");
                          onUseQnAChange({ target: { checked: true } });
                        }}
                      >
                        <span className="promptTypeOptionValue">
                          {analysisSettingsConfig?.lblOptionGenNewQA}
                        </span>
                      </MotifRadioButton>
                      <MotifTooltip
                        className="tooltip-qa"
                        trigger={
                          <img
                            src={iconInfoEmpty}
                            alt="info"
                            className="iconInfo"
                          />
                        }
                        allowHover={true}
                        allowClick={false}
                        hideCloseButton={true}
                        placement="right"
                        contentClassName="tooltipWithoutClose prompt-tooltip-no"
                      >
                        <div className="tooltip-hover-div">
                          {analysisSettingsConfig?.msgTooltipOptionGenNewQA}
                        </div>
                      </MotifTooltip>
                    </div>
                    <div className="fldPromptTypeRadio">
                      <MotifRadioButton
                        className="fldPromptTypeOption"
                        value="useExist"
                        checked={selectedQAValue === "useExist"}
                        disabled={
                          initSource !== "upload" &&
                          !checkRowIds.some((rowId) =>
                            rowStatusData.some(
                              (row) =>
                                row.rowId === rowId && row.existingQnA === true
                            )
                          )
                        }
                        onChange={() => {
                          handleChangeQA("useExist");
                          onUseQnAChange({ target: { checked: false } }); // Simulates "Use Q&A in analysis" unchecked
                        }}
                      >
                        <span className="promptTypeOptionValue">
                          {analysisSettingsConfig?.lblOptionUseExisting}
                        </span>
                      </MotifRadioButton>
                      <MotifTooltip
                        className="tooltip-qa"
                        trigger={
                          <img
                            src={iconInfoEmpty}
                            alt="info"
                            className="iconInfo"
                          />
                        }
                        allowHover={true}
                        allowClick={false}
                        hideCloseButton={true}
                        placement="right"
                        contentClassName="tooltipWithoutClose prompt-tooltip-no"
                      >
                        <div className="tooltip-hover-div">
                          {analysisSettingsConfig?.msgTooltipOptionUseExisting}
                        </div>
                      </MotifTooltip>
                    </div>
                  </MotifRadioButtonGroup>
                </MotifFormField>
              </div>
            )}
            {
              selectedAnalysisValue &&
              (
                (selectedAnalysisValue === "notes") ||
                (selectedQAValue === "genNew") ||
                (selectedQAValue === "useExist") ||
                (selectedAnalysisValue === "custom")
              ) && (
                <>
                  <div className="divider"></div>
                  <div className="fldPromptType">
                    <MotifFormField>
                      <MotifLabel className="fldLabel">
                        {analysisSettingsConfig?.lblPromptLibrary}
                      </MotifLabel>
                      <MotifRadioButtonGroup className="fldPromptTypeGroup">
                        {promptTypeRef.map((element) => (
                          <div
                            key={element.id}
                            className="promptTypeOptionWrapper prompt-div"
                          >
                            <div>
                              <MotifRadioButton
                                id={"fldPromptType" + element.key}
                                name="fldPromptTypeOption"
                                onChange={() =>
                                  onPromptTypeChange(element.value)
                                }
                                value={element.value}
                                className="fldPromptTypeOption"
                                checked={selectedPromptType === element.value}
                                disabled={
                                  element.key === "Personal" &&
                                  analysisSettings?.personalPrompt.length < 1
                                }
                              >
                                <span
                                  className={
                                    element.key === "Personal" &&
                                    analysisSettings?.personalPrompt.length < 1
                                      ? "promptTypeOptionValue"
                                      : ""
                                  }
                                >
                                  {element.label}
                                </span>
                              </MotifRadioButton>
                            </div>
                            <div>
                              {element.key === "Personal" &&
                                analysisSettings?.personalPrompt.length < 1 && (
                                  <MotifTooltip
                                    trigger={
                                      <img
                                        src={iconInfoEmpty}
                                        alt="info"
                                        className="iconInfo"
                                      />
                                    }
                                    allowHover={true}
                                    allowClick={true}
                                    hideCloseButton={true}
                                    placement="right"
                                    contentClassName="tooltipWithoutClose prompt-tooltip-no"
                                  >
                                    {analysisSettingsConfig?.msgNoPersonalPrompts}
                                  </MotifTooltip>
                                )}
                            </div>
                          </div>
                        ))}
                      </MotifRadioButtonGroup>
                    </MotifFormField>
                  </div>
                  <div className="fldPromptName">
                    <MotifFormField>
                      <MotifLabel className="fldLabel">{analysisSettingsConfig?.lblPromptName}</MotifLabel>
                      {!isMultiplePrompts && (
                        <span className="fldValPromptName">
                          {selectedPrompt?.promptName}
                        </span>
                      )}
                      {isMultiplePrompts &&
                        analysisSettings?.personalPrompt.length > 1 && (
                          <MotifSelect
                            placeholder="Select prompt"
                            visibleOptions={3}
                            className="fldPromptNameSelect"
                          >
                            {analysisSettings.personalPrompt.map((element) => (
                              <MotifOption
                                onClick={() => onPromptNameChange(element)}
                                value={element?.promptName}
                                key={element.id}
                              >
                                {element.promptName}
                              </MotifOption>
                            ))}
                          </MotifSelect>
                        )}
                    </MotifFormField>
                  </div>
                </>
              )}
          </>
        )}
        {currentStep === 2 && !isCallForTableAnalysis && (
          <>
            <MotifTabNavigation
              defaultActiveKey={0}
              className="docsTab"
              variant="alt"
            >
              {vTabs.map((vTab) => (
                <MotifTabControl
                  key={vTab.id}
                  onClick={() => handleTabClick(vTab)}
                  className="btnDocsTab"
                  disabled={
                    vTab.elementId === "idTabContentDeleted" &&
                    delDocuments.length === 0
                  }
                >
                  <span className="tabTitle">{vTab.title}</span>
                  {vTab.elementId === "idTabContentDeleted" &&
                    delDocuments.length === 0 && (
                      <MotifTooltip
                        trigger={
                          <img src={iconInfo} alt="info" className="iconInfo" />
                        }
                        allowHover={true}
                        allowClick={false}
                        hideCloseButton={true}
                        contentClassName="tooltipWithoutClose"
                      >
                        No deleted <br />
                        documents found
                      </MotifTooltip>
                    )}
                </MotifTabControl>
              ))}
            </MotifTabNavigation>
            <div className="doc-search-qa">
              <div className="tabContentActive" id="idTabContentActive">
                {/* Search Bar Section */}
                {documents.length !== 0 && (
                  <div className="search-section">
                    <MotifFormField className="search-wrapper-doc">
                      <MotifSearch
                        aria-label="Search"
                        placeholder="Search"
                        value={searchValue}
                        onChange={handleSearchChange}
                        searchButtonProps={{ hidden: true }}
                      />
                      <img
                        src={iconSearch}
                        alt="Search"
                        className="iconSearch"
                      />
                    </MotifFormField>
                  </div>
                )}
                {/* Select All Section */}
                <div className="documentsContainer">
                  <div
                    className={
                      filterdDocuments.length === 0
                        ? "scroll-bar1"
                        : "scroll-bar"
                    }
                  >
                    {searchValue === "" && documents.length > 0 && (
                      <div className="gridItem">
                        <MotifCheckbox
                          className="selectAll"
                          id="select-all"
                          value={analysisSettingsConfig?.lblSelectAll}
                          onChange={handleSelectAll}
                          checked={
                            selectedDocuments.length ===
                              filterdDocuments.length &&
                            filterdDocuments.length > 0
                          }
                          disabled={
                            isCheckBoxDisabled || filterdDocuments.length === 0
                          }
                        >
                          {analysisSettingsConfig?.lblSelectAll}
                        </MotifCheckbox>
                      </div>
                    )}
                    {/* Document Items Section */}
                    <div className="gridItem">
                      {documents.length === 0 && (
                        <div className="page-search">
                          <img
                            src={iconPageSearch}
                            alt="Search"
                            className="pageSearchIcon"
                          />
                          <MotifMessage className="no-doc-message">
                            <div className="doc-rel-text">
                              {analysisSettingsConfig?.msgNoDocs}
                            </div>
                            {analysisSettingsConfig?.msgNoDocsInfo}
                          </MotifMessage>
                        </div>
                      )}
                      {documents.length !== 0 &&
                        filterdDocuments.length === 0 && (
                          <div className="no-documents-found">
                            <img
                              src={iconSearch}
                              alt="Search"
                              className="iconSearch"
                            />
                            <MotifMessage className="no-doc-found">
                              {analysisSettingsConfig?.msgNoSearchDocs}
                            </MotifMessage>
                          </div>
                        )}
                      {documents.length !== 0 &&
                        filterdDocuments.length !== 0 && (
                          <div className="documentsGrid">
                            {filterdDocuments.map(
                              (docs) =>
                                docs.status === "Complete" && (
                                  <MotifCheckbox
                                    className="docs-border"
                                    disabled={isCheckBoxDisabled}
                                    key={docs.documentId}
                                    id={docs.documentId}
                                    value={docs.documentName}
                                    onChange={(e) => {
                                      handleDocumentSelectList(docs);
                                    }}
                                    checked={
                                      selectedDocuments.findIndex(
                                        (element) =>
                                          element.document_UUID ===
                                          docs.document_UUID
                                      ) !== -1
                                    }
                                  >
                                    {docs.documentName}
                                  </MotifCheckbox>
                                )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="tabContentDeleted"
                style={{ display: "none" }}
                id="idTabContentDeleted"
              >
                {delDocuments.map((doc, i) => (
                  <div className="doc-list" key={doc.documentId}>
                    <div className="document-item deleted">
                      <div className="document-name">{doc.documentName}</div>
                    </div>
                  </div>
                ))}
                <div className="tabContentDeletedContent">
                  {analysisSettingsConfig?.msgDeletedDocs}
                </div>
              </div>
            </div>
          </>
        )}
        {currentStep === 2 && isCallForTableAnalysis && (
          <div className="lineitemlisting">
            <div>
              <span className="listingtext">{analysisSettingsConfig?.lblLineItems}</span>
            </div>
            <div className="list-grid-container">
              <MotifTable
                className="listanalysis"
                rowData={completedItems}
                columnDefs={colDefs}
                rowSelection="multiple"
                suppressCellFocus
                suppressRowClickSelection
                onRowSelected={(event) => onLineItemSelection(event)}
                onGridReady={(params) => {
                  const gridApi = params.api;
                  gridApi.forEachNode((node) => {
                    if (node.data.selected === true) {
                      node.setSelected(true);
                    }
                  });
                }}
              />
            </div>
          </div>
        )}
      </MotifModalBody>
      <MotifModalFooter
        className={
          currentStep === 2 && isCallForTableAnalysis
            ? "tableAnalysisFooter"
            : "analysisSettingsFooter"
        }
      >
        {currentStep === 2 && showMessage && !isCheckBoxDisabled && (
          <MotifToast
            id="docWarning"
            variant="warning"
            actionName=""
            position=""
            onClose={() => {
              onToastcloseclick();
            }}
            className="docWarning"
            icons={{
              toast: <img src={iconWarning} alt="Warning" />,
            }}
          >
            <div>
              {analysisSettingsConfig?.msgToastSelectedAllDocs}
            </div>
          </MotifToast>
        )}
        {currentStep === 2 && showMessage && isCheckBoxDisabled && (
          <MotifToast
            id="docWarning"
            variant="warning"
            actionName=""
            position=""
            onClose={() => {
              onToastcloseclick();
            }}
            className="docWarning"
            icons={{
              toast: <img src={iconWarning} alt="Warning" />,
            }}
          >
            <div>
              {analysisSettingsConfig?.msgToastLAInProgress}
            </div>
          </MotifToast>
        )}
        <div className="btn-grp-qa1">
          {currentStep === 1 && !isCallForTableAnalysis && (
            <>
              {
                ((selectedAnalysisValue === "qa") || (selectedAnalysisValue === "custom")) ? (
                <>
                  <MotifButton
                    onClick={onClose}
                    type="button"
                    variant="secondary"
                    className="btnCancel"
                  >
                    {analysisSettingsConfig?.btnLabelClose}
                  </MotifButton>
                  <MotifTooltip
                    trigger={
                      <MotifButton
                        type="button"
                        className="btnNext"
                        disabled={
                          !selectedPromptType ||
                          !selectedPrompt ||
                          isNextButtonDisabled ||
                          !selectedAnalysisValue ||
                          ((selectedAnalysisValue !== "custom") && !selectedQAValue)
                        }
                        onClick={handleNextClick}
                      >
                        {analysisSettingsConfig?.btnLabelNext}
                      </MotifButton>
                    }
                    allowHover={!selectedPrompt}
                    allowClick={false}
                    hideCloseButton={true}
                    placement="left"
                    contentClassName="tooltipWithoutClose"
                  >
                    {analysisSettingsConfig?.msgTooltipSelectPrompt}
                  </MotifTooltip>
                </>
              ) : (
                <div className="footer-btn-grp">
                  <div className="btn-grp">
                    {currentStep === 1 && !isCallForTableAnalysis && (
                      <MotifButton
                        onClick={onClose}
                        type="button"
                        variant="secondary"
                        className="btnClose"
                      >
                        {analysisSettingsConfig?.btnLabelClose}
                      </MotifButton>
                    )}
                    <MotifTooltip
                      trigger={
                        <MotifButton
                          type="button"
                          className="btnConfirm"
                          disabled={
                            !selectedAnalysisValue ||
                            !selectedPromptType ||
                            !selectedPrompt
                          }
                          onClick={handleConfirmClick}
                        >
                          {analysisSettingsConfig?.btnLabelConfirm}
                        </MotifButton>
                      }
                      allowHover={
                        !(
                          !selectedAnalysisValue ||
                          !selectedPromptType ||
                          !selectedPrompt ||
                          isConfirmDisabled
                        ) || !selectedPrompt
                      }
                      allowClick={false}
                      hideCloseButton={true}
                      placement="left"
                      contentClassName="tooltipWithoutClose"
                      hide={getNotesConfirmBtnTooltipText() === ""}
                    >
                      {getNotesConfirmBtnTooltipText()}
                    </MotifTooltip>
                  </div>
                </div>
              )}
            </>
          )}
          {currentStep === 1 && isCallForTableAnalysis && (
            <>
              <MotifButton
                onClick={onClose}
                type="button"
                variant="secondary"
                className="btnCancel"
              >
                {analysisSettingsConfig?.btnLabelCancel}
              </MotifButton>
              <MotifTooltip
                placement="left"
                hide={selectedPromptType && selectedPrompt} // Tooltip hides when both values are selected
                trigger={
                  <MotifButton
                    className="btnNext"
                    disabled={!selectedPromptType || !selectedPrompt} // Disable when any value is missing
                    onClick={handleNextClick}
                  >
                    {analysisSettingsConfig?.btnLabelNext}
                  </MotifButton>
                }
                contentClassName="tooltipWithoutClose"
                allowHover={true}
                allowClick={false}
                hideCloseButton={true}
              >
                {analysisSettingsConfig?.msgTooltipSelectPrompt}
              </MotifTooltip>
            </>
          )}
        </div>
        {currentStep === 3 && isCallForTableAnalysis === false && (
          <MotifButton
            onClick={handleBackClick}
            type="button"
            variant="secondary"
            className="btnBack"
          >
            {analysisSettingsConfig?.btnLabelBack}
          </MotifButton>
        )}
        <div className="footer-container-2">
          <div className="tableRelationshipFooterleftkpiback">
            {
              (currentStep === 2) &&
              (
                (
                  !isCallForTableAnalysis &&
                  (
                    (selectedAnalysisValue === "qa") ||
                    (selectedAnalysisValue === "custom")
                  )
                ) ||
                isCallForTableAnalysis
              ) && (
                <div className="back-btn-div">
                  <MotifButton
                    onClick={handleBackClick}
                    className="btnBackKPI"
                    data-testid="testIdBtnBack"
                    variant="text"
                  >
                    <img src={iconArrowBack} alt="Previous" />
                    <span className="back-arrow">{analysisSettingsConfig?.btnLabelBack}</span>
                  </MotifButton>
                </div>
              )}
          </div>
          <div className="cnfrm-cls-btn-2">
            {currentStep === 2 && (
              <MotifButton
                onClick={onClose}
                type="button"
                variant="secondary"
                className="btnCancel"
              >
                {analysisSettingsConfig?.btnLabelClose}
              </MotifButton>
            )}
            {currentStep === 2 && (
              <MotifTooltip
                trigger={
                  <MotifButton
                    type="button"
                    className="btnConfirm"
                    disabled={
                      isCallForTableAnalysis
                        ? selectedLineItemsRowIds.length === 0
                        : isConfirmDisabled
                    }
                    onClick={
                      !fromUploadProcess
                        ? handleConfirmClick
                        : handleConfirmForUploadQANFlow
                    }
                  >
                    {analysisSettingsConfig?.btnLabelConfirm}
                  </MotifButton>
                }
                allowHover={!isCallForTableAnalysis}
                allowClick={false}
                hideCloseButton={true}
                placement="left"
                contentClassName="tooltipWithoutClose"
              >
                {analysisSettingsConfig?.msgTooltipConfirm}
              </MotifTooltip>
            )}
          </div>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

AnalysisSettings.propTypes = {
  initSource: PropTypes.string,
  openModal: PropTypes.bool,
  setOpenModal: PropTypes.func,
  fromUploadProcess: PropTypes.bool,
  setOpenModalForUploadProgress: PropTypes.func,
  closeModal: PropTypes.func,
  onConfirm: PropTypes.func,
  lineAnalysisStatus: PropTypes.string,
  completedItems: PropTypes.array,
  selectedLineItemsRowIds: PropTypes.array,
  setSelectedLineItemsRowIds: PropTypes.func,
  documentList: PropTypes.array,
  isCallForTableAnalysis: PropTypes.bool,
  selectedTable: PropTypes.object,
  exportedFileName: PropTypes.string,
  receiveUploadQnAStatus: PropTypes.func,
  setDisplayUploadQAModal: PropTypes.func,
  setUploadQALoader: PropTypes.func,
  rowStatusData: PropTypes.array,
  checkRowIds: PropTypes.array,
  setSelectedAnalysisValue: PropTypes.func,
  selectedAnalysisValue: PropTypes.string,
  selectedQAValue: PropTypes.string,
  setSelectedQAValue: PropTypes.func,
};

export default AnalysisSettings;
